import type { ProductStatus } from "@unbox/api";
import z from "zod/v3";
import { getVideoStatusLabel } from "@/components/assurance/badges/video-status-badge";
import { tierToSeverityLabel } from "@/components/safety-config/risk-groups";
import { type ActionType, getLogActionLabel } from "@/lib/assurance/action-types";
import { PRODUCT_STATUS_LABELS } from "@/lib/products";
import { toTitleCase } from "@/utils/toTitleCase";
import { truncateWithEllipsis } from "@/utils/truncate";

const optionWithLabelSchema = z.object({
  id: z.string(),
  label: z.string(),
});

export const filtersSchema = z.object({
  riskTier: z.array(z.string()),
  actionTaken: z.array(z.string()),
  riskId: z.array(z.string()),
  safetyStatus: z.array(z.string()),
  creatorType: z.enum(["affiliate", "brand"]).optional(),
  gender: z.enum(["male", "female"]).optional(),
  age: z.array(z.enum(["18-24", "25-34", "35-44", "45-54", "55+"])),
  videoRiskStatus: z.enum(["__all", "no_risk", "has_risk"]).optional(),
  minVideosAtRiskPercent: z.string().optional(),
  maxVideosAtRiskPercent: z.string().optional(),
  minGmv: z.string().optional(),
  maxGmv: z.string().optional(),
  minFollowers: z.string().optional(),
  maxFollowers: z.string().optional(),
  minUnitsSold: z.string().optional(),
  maxUnitsSold: z.string().optional(),
  minRating: z.string().optional(),
  maxRating: z.string().optional(),
  product: z.array(optionWithLabelSchema),
  hooks: z.array(optionWithLabelSchema),
  tactics: z.array(optionWithLabelSchema),
  videoStyles: z.array(optionWithLabelSchema),
  sellingPoints: z.array(optionWithLabelSchema),
  productStatus: z.array(z.string()),
  creator: z.array(optionWithLabelSchema),
  creatorList: z.array(optionWithLabelSchema),

  // Creator tag filters (include = has ANY; exclude = has NONE)
  includeTags: z.array(optionWithLabelSchema),
  excludeTags: z.array(optionWithLabelSchema),

  // Creator cohort filters (a creator has one cohort; include = cohort is one of
  // these; exclude = cohort is none of these)
  includeCohorts: z.array(optionWithLabelSchema),
  excludeCohorts: z.array(optionWithLabelSchema),

  // Stock Status for products
  stockStatus: z.array(z.enum(["in_stock", "low_stock", "out_of_stock"])),

  // Product performance filters
  minVideos: z.string().optional(),
  maxVideos: z.string().optional(),
  minPrice: z.string().optional(),
  maxPrice: z.string().optional(),
  minStockLevel: z.string().optional(),
  maxStockLevel: z.string().optional(),

  // Product category
  category: z.array(optionWithLabelSchema),

  // Collaboration Type
  collaborationType: z.enum(["all", "open", "target"]).optional(),

  // Performance metrics
  minCtr: z.string().optional(),
  maxCtr: z.string().optional(),
  minCtor: z.string().optional(),
  maxCtor: z.string().optional(),
  minOrders: z.string().optional(),
  maxOrders: z.string().optional(),
  minRevenue: z.string().optional(),
  maxRevenue: z.string().optional(),
  minAov: z.string().optional(),
  maxAov: z.string().optional(),

  // Creator Overview
  // TikTok-defined advanced marketplace filters (opaque per-shop labels, e.g. "Lv. 1", "Home") —
  // plain strings, not a fixed enum, since TikTok can change the value set without notice.
  creatorLevel: z.array(z.string()),
  categoryPro: z.array(z.string()),
  creatorLanguage: z.array(z.string()),
  topSellingCategories: z.array(optionWithLabelSchema),
  minTotalLikes: z.string().optional(),
  maxTotalLikes: z.string().optional(),
  minPostingFrequency: z.string().optional(),
  maxPostingFrequency: z.string().optional(),

  // Creator Attributes
  toneAndApproach: z.array(optionWithLabelSchema),
  contentFocus: z.array(optionWithLabelSchema),
  productionQuality: z.array(optionWithLabelSchema),

  // Audience
  maleAudiencePercent: z.enum(["all", ">25", ">50", ">75"]).optional(),
  femaleAudiencePercent: z.enum(["all", ">25", ">50", ">75"]).optional(),

  // Candidate-specific filters
  collaborationStatus: z.enum(["all", "invited", "not_invited"]).optional(),
  minLiveGmv: z.string().optional(),
  maxLiveGmv: z.string().optional(),
  minVideoGmv: z.string().optional(),
  maxVideoGmv: z.string().optional(),

  // Video detected language
  videoLanguages: z.array(optionWithLabelSchema),

  // Video-specific filters
  minViews: z.string().optional(),
  maxViews: z.string().optional(),

  // Collaboration filter (for video explorer)
  collaboration: z.array(optionWithLabelSchema),

  // Campaign filter (for video & creator explorer)
  campaign: z.array(optionWithLabelSchema),

  // Publish date range filter (for video explorer)
  publishDateStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date, expected YYYY-MM-DD")
    .optional(),
  publishDateEnd: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date, expected YYYY-MM-DD")
    .optional(),

  // Posted date range filter (for creator explorer): keeps creators who published at
  // least one video inside the window, rather than scoping their metrics to it
  postedFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date, expected YYYY-MM-DD")
    .optional(),
  postedTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date, expected YYYY-MM-DD")
    .optional(),

  // Flagged date range filter (when the risk/flag was detected, not when the video was published)
  flaggedDateStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date, expected YYYY-MM-DD")
    .optional(),
  flaggedDateEnd: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date, expected YYYY-MM-DD")
    .optional(),

  // Time period filter (for video explorer metrics aggregation)
  timePeriod: z.enum(["7d", "30d", "all_time"]).optional(),

  // Sequence run filters
  sequenceRunSequence: optionWithLabelSchema.optional(),
  sequenceRunStatus: z.enum(["active", "completed", "cancelled", "failed"]).optional(),

  // Sample Requests filters (Operations)
  sampleBrandSafety: z.array(z.enum(["low", "high"])),
  minPostRate: z.string().optional(),
  maxPostRate: z.string().optional(),

  negated: z.array(z.string()),
});

export type FiltersFormValues = z.infer<typeof filtersSchema>;

export type FilterField = Exclude<keyof FiltersFormValues, "negated">;

export type FilterOperator = "is" | "is_not";

type AudiencePercentThreshold = ">25" | ">50" | ">75";

export interface FiltersApiValues {
  riskTier?: string[];
  actionTaken?: string[];
  riskId?: string[];
  safetyStatus?: string[];
  creatorType?: "affiliate" | "brand";
  gender?: "male" | "female";
  maleAudiencePercent?: AudiencePercentThreshold;
  femaleAudiencePercent?: AudiencePercentThreshold;
  age?: ("18-24" | "25-34" | "35-44" | "45-54" | "55+")[];
  videoRiskStatus?: "no_risk" | "has_risk";
  minVideosAtRiskPercent?: number;
  maxVideosAtRiskPercent?: number;
  minGmv?: number;
  maxGmv?: number;
  minLiveGmv?: number;
  maxLiveGmv?: number;
  minVideoGmv?: number;
  maxVideoGmv?: number;
  minFollowers?: number;
  maxFollowers?: number;
  minUnitsSold?: number;
  maxUnitsSold?: number;
  minRating?: number;
  maxRating?: number;
  productIds?: string[];
  hooks?: string[];
  tactics?: string[];
  videoStyles?: string[];
  sellingPoints?: string[];
  status?: ProductStatus[];
  creatorIds?: string[];
  creatorListIds?: string[];
  creatorClassifications?: string[];
  includeTagIds?: string[];
  excludeTagIds?: string[];
  includeCohortIds?: string[];
  excludeCohortIds?: string[];
  minCtr?: number;
  maxCtr?: number;
  minCtor?: number;
  maxCtor?: number;
  minOrders?: number;
  maxOrders?: number;
  minAov?: number;
  maxAov?: number;

  // Product filters
  stockStatus?: ("in_stock" | "low_stock" | "out_of_stock")[];
  minVideos?: number;
  maxVideos?: number;
  minPrice?: number;
  maxPrice?: number;
  minStockLevel?: number;
  maxStockLevel?: number;
  categoryIds?: string[];

  // Creator Attributes filters
  toneAndApproach?: string[];
  contentFocus?: string[];
  productionQuality?: string[];

  // Creator Overview filters
  topSellingCategories?: string[];
  minTotalLikes?: number;
  maxTotalLikes?: number;
  minPostingFrequency?: number;
  maxPostingFrequency?: number;

  // TikTok-defined advanced marketplace filters
  creatorLevel?: string[];
  categoryPro?: string[];
  creatorLanguage?: string[];

  // Collaboration Type filter
  collaborationType?: "open" | "target";

  // Collaboration Status filter (Candidate Explorer)
  collaborationStatus?: "invited" | "not_invited";

  // Video detected language
  videoLanguages?: string[];

  // Video-specific filters
  minViews?: number;
  maxViews?: number;
  collaborationIds?: string[];
  campaignIds?: string[];
  postedFrom?: string;
  postedTo?: string;
  publishDateStart?: string;
  publishDateEnd?: string;
  flaggedDateStart?: string;
  flaggedDateEnd?: string;

  // Sequence run filters
  sequenceRunSequenceId?: string;
  sequenceRunStatus?: "active" | "completed" | "cancelled" | "failed";

  // Sample Requests filters (Operations)
  brandSafety?: ("low" | "high")[];
  minPostRate?: number;
  maxPostRate?: number;

  not?: string[];
}

export const toNotQuery = <K extends string>(
  not: readonly string[] | undefined,
  negatable: readonly K[],
): K[] | undefined => {
  const keys = [...new Set(not)].filter((key): key is K => (negatable as readonly string[]).includes(key));
  return keys.length > 0 ? keys : undefined;
};

const isEmptyFilterValue = (value: unknown) =>
  value === "__all" ||
  value === "all" ||
  value === "all_time" ||
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

export const removeOperatorFromEmptyFilters = (
  values: FiltersFormValues,
  activeFields: FilterField[],
  changedField: FilterField,
): string[] => {
  const changedPillId = pillIdOf(changedField);
  const activePillIds = new Set(getActivePills(values, activeFields).map((pill) => pill.id));
  return (values.negated ?? []).filter((pillId) => pillId !== changedPillId || activePillIds.has(pillId));
};

const normalizeNotKey = (notKey: NotKey | undefined): readonly string[] =>
  notKey === undefined ? [] : typeof notKey === "string" ? [notKey] : notKey;

const buildNotParam = (values: FiltersFormValues, activeFields: FilterField[]): string[] => {
  const negated = new Set(values.negated ?? []);
  const keys = new Set<string>();
  for (const pill of getActivePills(values, activeFields)) {
    if (!negated.has(pill.id)) continue;
    for (const key of normalizeNotKey(pill.notKey)) keys.add(key);
  }
  return [...keys];
};

export const transformFilters = (values: FiltersFormValues, activeFields: FilterField[]): FiltersApiValues => {
  const filters: FiltersApiValues = {};

  for (const field of activeFields) {
    switch (field) {
      case "riskTier":
        filters.riskTier = values.riskTier;
        break;
      case "actionTaken":
        filters.actionTaken = values.actionTaken;
        break;
      case "riskId":
        filters.riskId = values.riskId;
        break;
      case "safetyStatus":
        filters.safetyStatus = values.safetyStatus.length > 0 ? values.safetyStatus : undefined;
        break;
      case "hooks":
        filters.hooks = values.hooks.length > 0 ? values.hooks.map((c) => c.id) : undefined;
        break;
      case "tactics":
        filters.tactics = values.tactics.length > 0 ? values.tactics.map((c) => c.id) : undefined;
        break;
      case "sellingPoints":
        filters.sellingPoints = values.sellingPoints.length > 0 ? values.sellingPoints.map((c) => c.id) : undefined;
        break;
      case "videoStyles":
        filters.videoStyles = values.videoStyles.length > 0 ? values.videoStyles.map((c) => c.id) : undefined;
        break;
      case "creatorType":
        filters.creatorType = values.creatorType ?? undefined;
        break;
      case "gender":
        filters.gender = values.gender ?? undefined;
        break;
      case "videoRiskStatus": {
        const value = values.videoRiskStatus;
        filters.videoRiskStatus = value && value !== "__all" ? value : undefined;
        break;
      }
      case "minVideosAtRiskPercent":
        filters.minVideosAtRiskPercent = values.minVideosAtRiskPercent
          ? Number.parseFloat(values.minVideosAtRiskPercent) || undefined
          : undefined;
        break;
      case "maxVideosAtRiskPercent":
        filters.maxVideosAtRiskPercent = values.maxVideosAtRiskPercent
          ? Number.parseFloat(values.maxVideosAtRiskPercent) || undefined
          : undefined;
        break;
      case "age":
        filters.age = values.age.length > 0 ? values.age : undefined;
        break;
      case "minGmv":
        filters.minGmv = values.minGmv ? Number.parseFloat(values.minGmv) || undefined : undefined;
        break;
      case "maxGmv": {
        const maxGmvValue = values.maxGmv ? Number.parseFloat(values.maxGmv) || undefined : undefined;
        const minGmvValue = values.minGmv ? Number.parseFloat(values.minGmv) || undefined : undefined;

        // When min === max (exact match filtering), apply tolerance to account for display rounding.
        // Values displayed as "£33K" could be anywhere from 32,500 to 33,499.
        // Without tolerance, filtering for "£33K" (parsed as 33000) would miss "£33,456" because
        // the query "33456 >= 33000 AND 33456 <= 33000" is false.
        if (maxGmvValue !== undefined && minGmvValue !== undefined && maxGmvValue === minGmvValue) {
          // Add tolerance based on the magnitude of the value
          // For values >= 1M: tolerance of 50K (values rounding to same M)
          // For values >= 1K: tolerance of 500 (values rounding to same K)
          // For values < 1K: tolerance of 5 (small values)
          let tolerance: number;
          if (maxGmvValue >= 1_000_000) {
            tolerance = 50_000;
          } else if (maxGmvValue >= 1_000) {
            tolerance = 500;
          } else {
            tolerance = 5;
          }
          filters.maxGmv = maxGmvValue + tolerance;
        } else {
          filters.maxGmv = maxGmvValue;
        }
        break;
      }
      case "minFollowers":
        filters.minFollowers = values.minFollowers ? Number.parseFloat(values.minFollowers) || undefined : undefined;
        break;
      case "maxFollowers":
        filters.maxFollowers = values.maxFollowers ? Number.parseFloat(values.maxFollowers) || undefined : undefined;
        break;
      case "minUnitsSold":
        filters.minUnitsSold = values.minUnitsSold ? Number.parseFloat(values.minUnitsSold) || undefined : undefined;
        break;
      case "maxUnitsSold":
        filters.maxUnitsSold = values.maxUnitsSold ? Number.parseFloat(values.maxUnitsSold) || undefined : undefined;
        break;
      case "minRating":
        filters.minRating = values.minRating ? Number.parseFloat(values.minRating) || undefined : undefined;
        break;
      case "maxRating":
        filters.maxRating = values.maxRating ? Number.parseFloat(values.maxRating) || undefined : undefined;
        break;
      case "product":
        filters.productIds = values.product?.length > 0 ? values.product.map((product) => product.id) : undefined;
        break;
      case "productStatus":
        filters.status = values.productStatus as ProductStatus[];
        break;
      case "creator":
        filters.creatorIds = values.creator.length > 0 ? values.creator.map((c) => c.id) : undefined;
        break;
      case "creatorList": {
        const allItems = values.creatorList;
        const unboxItems = allItems.filter((c) => c.id.startsWith("unbox:")).map((c) => c.id.slice("unbox:".length));
        const listIds = allItems.filter((c) => !c.id.startsWith("unbox:")).map((c) => c.id);
        filters.creatorListIds = listIds.length > 0 ? listIds : undefined;
        filters.creatorClassifications = unboxItems.length > 0 ? unboxItems : undefined;
        break;
      }
      case "includeTags":
        // Empty Include applies no filter (not "creators with zero tags")
        filters.includeTagIds = values.includeTags.length > 0 ? values.includeTags.map((t) => t.id) : undefined;
        break;
      case "excludeTags":
        filters.excludeTagIds = values.excludeTags.length > 0 ? values.excludeTags.map((t) => t.id) : undefined;
        break;
      case "includeCohorts":
        // Empty Include applies no filter (not "creators with no cohort")
        filters.includeCohortIds =
          values.includeCohorts.length > 0 ? values.includeCohorts.map((c) => c.id) : undefined;
        break;
      case "excludeCohorts":
        filters.excludeCohortIds =
          values.excludeCohorts.length > 0 ? values.excludeCohorts.map((c) => c.id) : undefined;
        break;
      case "minCtr":
        filters.minCtr = values.minCtr ? Number.parseFloat(values.minCtr) || undefined : undefined;
        break;
      case "maxCtr": {
        const maxCtrValue = values.maxCtr ? Number.parseFloat(values.maxCtr) || undefined : undefined;
        const minCtrValue = values.minCtr ? Number.parseFloat(values.minCtr) || undefined : undefined;
        // Apply tolerance for exact match filtering (same as GMV)
        if (maxCtrValue !== undefined && minCtrValue !== undefined && maxCtrValue === minCtrValue) {
          filters.maxCtr = maxCtrValue + 0.5; // 0.5% tolerance for percentages
        } else {
          filters.maxCtr = maxCtrValue;
        }
        break;
      }
      case "minCtor":
        filters.minCtor = values.minCtor ? Number.parseFloat(values.minCtor) || undefined : undefined;
        break;
      case "maxCtor": {
        const maxCtorValue = values.maxCtor ? Number.parseFloat(values.maxCtor) || undefined : undefined;
        const minCtorValue = values.minCtor ? Number.parseFloat(values.minCtor) || undefined : undefined;
        // Apply tolerance for exact match filtering
        if (maxCtorValue !== undefined && minCtorValue !== undefined && maxCtorValue === minCtorValue) {
          filters.maxCtor = maxCtorValue + 0.5; // 0.5% tolerance for percentages
        } else {
          filters.maxCtor = maxCtorValue;
        }
        break;
      }
      case "minOrders":
        filters.minOrders = values.minOrders ? Number.parseFloat(values.minOrders) || undefined : undefined;
        break;
      case "maxOrders": {
        const maxOrdersValue = values.maxOrders ? Number.parseFloat(values.maxOrders) || undefined : undefined;
        const minOrdersValue = values.minOrders ? Number.parseFloat(values.minOrders) || undefined : undefined;
        // Apply tolerance for exact match filtering
        if (maxOrdersValue !== undefined && minOrdersValue !== undefined && maxOrdersValue === minOrdersValue) {
          filters.maxOrders = maxOrdersValue + 1; // 1 order tolerance
        } else {
          filters.maxOrders = maxOrdersValue;
        }
        break;
      }
      case "minRevenue":
        // Revenue uses the same field as GMV on the backend
        break;
      case "maxRevenue":
        // Revenue uses the same field as GMV on the backend
        break;
      case "minAov":
        filters.minAov = values.minAov ? Number.parseFloat(values.minAov) || undefined : undefined;
        break;
      case "maxAov": {
        const maxAovValue = values.maxAov ? Number.parseFloat(values.maxAov) || undefined : undefined;
        const minAovValue = values.minAov ? Number.parseFloat(values.minAov) || undefined : undefined;
        // Apply tolerance for exact match filtering (same logic as GMV)
        if (maxAovValue !== undefined && minAovValue !== undefined && maxAovValue === minAovValue) {
          let tolerance: number;
          if (maxAovValue >= 1_000_000) {
            tolerance = 50_000;
          } else if (maxAovValue >= 1_000) {
            tolerance = 500;
          } else {
            tolerance = 5;
          }
          filters.maxAov = maxAovValue + tolerance;
        } else {
          filters.maxAov = maxAovValue;
        }
        break;
      }
      case "minLiveGmv":
        filters.minLiveGmv = values.minLiveGmv ? Number.parseFloat(values.minLiveGmv) || undefined : undefined;
        break;
      case "maxLiveGmv": {
        const maxLiveGmvValue = values.maxLiveGmv ? Number.parseFloat(values.maxLiveGmv) || undefined : undefined;
        const minLiveGmvValue = values.minLiveGmv ? Number.parseFloat(values.minLiveGmv) || undefined : undefined;
        // Apply tolerance for exact match filtering (same logic as GMV)
        if (maxLiveGmvValue !== undefined && minLiveGmvValue !== undefined && maxLiveGmvValue === minLiveGmvValue) {
          let tolerance: number;
          if (maxLiveGmvValue >= 1_000_000) {
            tolerance = 50_000;
          } else if (maxLiveGmvValue >= 1_000) {
            tolerance = 500;
          } else {
            tolerance = 5;
          }
          filters.maxLiveGmv = maxLiveGmvValue + tolerance;
        } else {
          filters.maxLiveGmv = maxLiveGmvValue;
        }
        break;
      }
      case "minVideoGmv":
        filters.minVideoGmv = values.minVideoGmv ? Number.parseFloat(values.minVideoGmv) || undefined : undefined;
        break;
      case "maxVideoGmv": {
        const maxVideoGmvValue = values.maxVideoGmv ? Number.parseFloat(values.maxVideoGmv) || undefined : undefined;
        const minVideoGmvValue = values.minVideoGmv ? Number.parseFloat(values.minVideoGmv) || undefined : undefined;
        // Apply tolerance for exact match filtering (same logic as GMV)
        if (maxVideoGmvValue !== undefined && minVideoGmvValue !== undefined && maxVideoGmvValue === minVideoGmvValue) {
          let tolerance: number;
          if (maxVideoGmvValue >= 1_000_000) {
            tolerance = 50_000;
          } else if (maxVideoGmvValue >= 1_000) {
            tolerance = 500;
          } else {
            tolerance = 5;
          }
          filters.maxVideoGmv = maxVideoGmvValue + tolerance;
        } else {
          filters.maxVideoGmv = maxVideoGmvValue;
        }
        break;
      }
      // Product filters
      case "stockStatus":
        filters.stockStatus = values.stockStatus.length > 0 ? values.stockStatus : undefined;
        break;
      case "minVideos":
        filters.minVideos = values.minVideos ? Number.parseFloat(values.minVideos) || undefined : undefined;
        break;
      case "maxVideos": {
        const maxVideosValue = values.maxVideos ? Number.parseFloat(values.maxVideos) || undefined : undefined;
        const minVideosValue = values.minVideos ? Number.parseFloat(values.minVideos) || undefined : undefined;
        if (maxVideosValue !== undefined && minVideosValue !== undefined && maxVideosValue === minVideosValue) {
          filters.maxVideos = maxVideosValue + 1;
        } else {
          filters.maxVideos = maxVideosValue;
        }
        break;
      }
      case "minPrice":
        filters.minPrice = values.minPrice ? Number.parseFloat(values.minPrice) || undefined : undefined;
        break;
      case "maxPrice": {
        const maxPriceValue = values.maxPrice ? Number.parseFloat(values.maxPrice) || undefined : undefined;
        const minPriceValue = values.minPrice ? Number.parseFloat(values.minPrice) || undefined : undefined;
        if (maxPriceValue !== undefined && minPriceValue !== undefined && maxPriceValue === minPriceValue) {
          let tolerance: number;
          if (maxPriceValue >= 1_000) {
            tolerance = 50;
          } else {
            tolerance = 5;
          }
          filters.maxPrice = maxPriceValue + tolerance;
        } else {
          filters.maxPrice = maxPriceValue;
        }
        break;
      }
      case "minStockLevel":
        filters.minStockLevel = values.minStockLevel ? Number.parseFloat(values.minStockLevel) || undefined : undefined;
        break;
      case "maxStockLevel": {
        const maxStockValue = values.maxStockLevel ? Number.parseFloat(values.maxStockLevel) || undefined : undefined;
        const minStockValue = values.minStockLevel ? Number.parseFloat(values.minStockLevel) || undefined : undefined;
        if (maxStockValue !== undefined && minStockValue !== undefined && maxStockValue === minStockValue) {
          filters.maxStockLevel = maxStockValue + 1;
        } else {
          filters.maxStockLevel = maxStockValue;
        }
        break;
      }
      case "category":
        filters.categoryIds = values.category?.map((c) => c.id) ?? undefined;
        break;
      case "maleAudiencePercent":
        if (values.maleAudiencePercent && values.maleAudiencePercent !== "all") {
          filters.maleAudiencePercent = values.maleAudiencePercent as AudiencePercentThreshold;
        }
        break;
      case "femaleAudiencePercent":
        if (values.femaleAudiencePercent && values.femaleAudiencePercent !== "all") {
          filters.femaleAudiencePercent = values.femaleAudiencePercent as AudiencePercentThreshold;
        }
        break;
      case "toneAndApproach":
        filters.toneAndApproach =
          values.toneAndApproach?.length > 0 ? values.toneAndApproach.map((t) => t.id) : undefined;
        break;
      case "contentFocus":
        filters.contentFocus = values.contentFocus?.length > 0 ? values.contentFocus.map((c) => c.id) : undefined;
        break;
      case "productionQuality":
        filters.productionQuality =
          values.productionQuality?.length > 0 ? values.productionQuality.map((p) => p.id) : undefined;
        break;
      case "topSellingCategories":
        filters.topSellingCategories =
          values.topSellingCategories?.length > 0 ? values.topSellingCategories.map((c) => c.id) : undefined;
        break;
      case "creatorLevel":
        filters.creatorLevel = values.creatorLevel.length > 0 ? values.creatorLevel : undefined;
        break;
      case "categoryPro":
        filters.categoryPro = values.categoryPro.length > 0 ? values.categoryPro : undefined;
        break;
      case "creatorLanguage":
        filters.creatorLanguage = values.creatorLanguage.length > 0 ? values.creatorLanguage : undefined;
        break;
      case "minTotalLikes":
        filters.minTotalLikes = values.minTotalLikes ? Number.parseFloat(values.minTotalLikes) || undefined : undefined;
        break;
      case "maxTotalLikes":
        filters.maxTotalLikes = values.maxTotalLikes ? Number.parseFloat(values.maxTotalLikes) || undefined : undefined;
        break;
      case "minPostingFrequency":
        filters.minPostingFrequency = values.minPostingFrequency
          ? Number.parseFloat(values.minPostingFrequency) || undefined
          : undefined;
        break;
      case "maxPostingFrequency":
        filters.maxPostingFrequency = values.maxPostingFrequency
          ? Number.parseFloat(values.maxPostingFrequency) || undefined
          : undefined;
        break;
      case "collaborationType":
        // "all" means no filter, only "open" or "target" are sent to API
        filters.collaborationType =
          values.collaborationType && values.collaborationType !== "all" ? values.collaborationType : undefined;
        break;
      case "collaborationStatus":
        // "all" means no filter, only "invited" or "not_invited" are sent to API
        filters.collaborationStatus =
          values.collaborationStatus && values.collaborationStatus !== "all" ? values.collaborationStatus : undefined;
        break;
      case "minViews":
        filters.minViews = values.minViews ? Number.parseFloat(values.minViews) || undefined : undefined;
        break;
      case "maxViews": {
        const maxViewsValue = values.maxViews ? Number.parseFloat(values.maxViews) || undefined : undefined;
        const minViewsValue = values.minViews ? Number.parseFloat(values.minViews) || undefined : undefined;
        if (maxViewsValue !== undefined && minViewsValue !== undefined && maxViewsValue === minViewsValue) {
          let tolerance: number;
          if (maxViewsValue >= 1_000_000) {
            tolerance = 50_000;
          } else if (maxViewsValue >= 1_000) {
            tolerance = 500;
          } else {
            tolerance = 5;
          }
          filters.maxViews = maxViewsValue + tolerance;
        } else {
          filters.maxViews = maxViewsValue;
        }
        break;
      }
      case "collaboration":
        // Remove 'open:' or 'target:' prefix from collaboration IDs
        filters.collaborationIds = values.collaboration?.map((c) => c.id.replace(/^(open|target):/, "")) ?? undefined;
        break;
      case "campaign":
        filters.campaignIds = values.campaign?.map((c) => c.id) ?? undefined;
        break;
      case "postedFrom":
        filters.postedFrom = values.postedFrom || undefined;
        break;
      case "postedTo":
        filters.postedTo = values.postedTo || undefined;
        break;
      case "publishDateStart":
        filters.publishDateStart = values.publishDateStart || undefined;
        break;
      case "publishDateEnd":
        filters.publishDateEnd = values.publishDateEnd || undefined;
        break;
      case "flaggedDateStart":
        filters.flaggedDateStart = values.flaggedDateStart || undefined;
        break;
      case "flaggedDateEnd":
        filters.flaggedDateEnd = values.flaggedDateEnd || undefined;
        break;
      case "sequenceRunSequence":
        filters.sequenceRunSequenceId = values.sequenceRunSequence?.id ?? undefined;
        break;
      case "sequenceRunStatus":
        filters.sequenceRunStatus = values.sequenceRunStatus ?? undefined;
        break;
      case "videoLanguages":
        filters.videoLanguages = values.videoLanguages.length > 0 ? values.videoLanguages.map((l) => l.id) : undefined;
        break;
      case "timePeriod":
        break;
      case "sampleBrandSafety":
        filters.brandSafety = values.sampleBrandSafety.length > 0 ? values.sampleBrandSafety : undefined;
        break;
      case "minPostRate":
        filters.minPostRate = values.minPostRate ? Number.parseFloat(values.minPostRate) || undefined : undefined;
        break;
      case "maxPostRate":
        filters.maxPostRate = values.maxPostRate ? Number.parseFloat(values.maxPostRate) || undefined : undefined;
        break;
    }
  }

  const not = buildNotParam(values, activeFields);
  if (not.length > 0) filters.not = not;

  return filters;
};

// Pills

export type FilterArrayDisplayMode = "first-with-count" | "join-all";

export type NotKey = string | readonly string[];

export interface FilterFieldConfig {
  label: string;
  notKey?: NotKey;
  formatter?: (value: unknown, currency?: "USD" | "GBP" | "EUR") => string;
  arrayDisplay?: FilterArrayDisplayMode;
  hoverLabel?: (value: unknown, currency?: "USD" | "GBP" | "EUR") => string;
}

const formatCurrency = (value: unknown, currency: "USD" | "GBP" | "EUR" = "USD") => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? formatter.format(parsed) : "";
};
export const RISK_TIER_LABELS: Record<string, string> = Object.fromEntries(
  [1, 2, 3, 4].map((t) => [String(t), tierToSeverityLabel(t)]),
);

export const pillsConfig: Record<FilterField, FilterFieldConfig> = {
  riskTier: {
    label: "Risk Severity",
    notKey: "riskTier",
    formatter: (value) => RISK_TIER_LABELS[String(value)] ?? `T${value}`,
    arrayDisplay: "join-all",
  },
  actionTaken: {
    label: "Action Type",
    notKey: "actionTaken",
    formatter: (value) => getLogActionLabel(value as ActionType),
    arrayDisplay: "join-all",
  },
  riskId: {
    label: "Risks in Video",
    notKey: "riskId",
    arrayDisplay: "join-all",
  },
  safetyStatus: {
    label: "Safety Status",
    notKey: "safetyStatus",
    formatter: (value) => getVideoStatusLabel(String(value)),
    arrayDisplay: "join-all",
  },
  creatorType: {
    label: "Content Owner",
    formatter: (value) => (value === "affiliate" ? "Creators" : "Brand"),
  },
  gender: {
    label: "Audience Gender",
    notKey: "gender",
    formatter: (value) => toTitleCase(String(value)),
  },
  age: {
    label: "Audience Age",
    notKey: "age",
    arrayDisplay: "join-all",
  },
  videoRiskStatus: {
    label: "Video Risk Status",
    formatter: (value) => (value === "has_risk" ? "Has Risks" : value === "no_risk" ? "No Risks" : "All"),
  },
  minVideosAtRiskPercent: {
    label: "Min % Videos at Risk",
    formatter: (value) => `${value}%`,
  },
  maxVideosAtRiskPercent: {
    label: "Max % Videos at Risk",
    formatter: (value) => `${value}%`,
  },
  hooks: {
    label: "Hooks",
    notKey: "hooks",
    formatter: (value) => (value as { label: string }).label,
  },
  tactics: {
    label: "Tactics",
    notKey: "tactics",
    formatter: (value) => (value as { label: string }).label,
  },
  sellingPoints: {
    label: "Selling Points",
    notKey: "sellingPoints",
    formatter: (value) => (value as { label: string }).label,
  },
  videoStyles: {
    label: "Video Styles",
    notKey: "videoStyles",
    formatter: (value) => (value as { label: string }).label,
  },
  minGmv: {
    label: "Min GMV",
    formatter: formatCurrency,
  },
  maxGmv: {
    label: "Max GMV",
    formatter: formatCurrency,
  },
  minFollowers: {
    label: "Min Followers",
    formatter: (value) => Number(value).toLocaleString(),
  },
  maxFollowers: {
    label: "Max Followers",
    formatter: (value) => Number(value).toLocaleString(),
  },
  minUnitsSold: {
    label: "Min Units Sold",
    formatter: (value) => Number(value).toLocaleString(),
  },
  maxUnitsSold: {
    label: "Max Units Sold",
    formatter: (value) => Number(value).toLocaleString(),
  },
  minRating: {
    label: "Min Rating",
    formatter: (value) => String(value),
  },
  maxRating: {
    label: "Max Rating",
    formatter: (value) => String(value),
  },
  product: {
    label: "Products",
    notKey: "productIds",
    formatter: (value) => truncateWithEllipsis((value as { label: string }).label, 20),
    arrayDisplay: "join-all",
    hoverLabel: (value) => (value as { label: string }).label,
  },
  productStatus: {
    label: "Product Status",
    notKey: "status",
    formatter: (value) => PRODUCT_STATUS_LABELS[value as ProductStatus] ?? "Unknown",
    arrayDisplay: "join-all",
  },
  creator: {
    label: "Creator",
    notKey: "creatorIds",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  creatorList: {
    label: "Creator List",
    notKey: ["creatorListIds", "creatorClassifications"],
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  includeTags: {
    label: "Include Tags",
    notKey: "includeTagIds",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  excludeTags: {
    label: "Exclude Tags",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  includeCohorts: {
    label: "Include Cohorts",
    notKey: "includeCohortIds",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  excludeCohorts: {
    label: "Exclude Cohorts",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },

  // New Creator Explorer fields
  collaborationType: {
    label: "Collaboration Type",
    notKey: "collaborationType",
    formatter: (value) => toTitleCase(String(value)),
  },
  minCtr: {
    label: "Min CTR",
    formatter: (value) => `${value}%`,
  },
  maxCtr: {
    label: "Max CTR",
    formatter: (value) => `${value}%`,
  },
  minCtor: {
    label: "Min CTOR",
    formatter: (value) => `${value}%`,
  },
  maxCtor: {
    label: "Max CTOR",
    formatter: (value) => `${value}%`,
  },
  minOrders: {
    label: "Min Orders",
    formatter: (value) => Number(value).toLocaleString(),
  },
  maxOrders: {
    label: "Max Orders",
    formatter: (value) => Number(value).toLocaleString(),
  },
  minRevenue: {
    label: "Min Revenue",
    formatter: formatCurrency,
  },
  maxRevenue: {
    label: "Max Revenue",
    formatter: formatCurrency,
  },
  minAov: {
    label: "Min AOV",
    formatter: formatCurrency,
  },
  maxAov: {
    label: "Max AOV",
    formatter: formatCurrency,
  },
  creatorLevel: {
    label: "Creator Level",
    notKey: "creatorLevel",
    arrayDisplay: "join-all",
  },
  categoryPro: {
    label: "Category Pro",
    notKey: "categoryPro",
    arrayDisplay: "join-all",
  },
  creatorLanguage: {
    label: "Creator Language",
    notKey: "creatorLanguage",
    arrayDisplay: "join-all",
  },
  topSellingCategories: {
    label: "Top Selling Categories",
    notKey: "topSellingCategories",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  minTotalLikes: {
    label: "Min Total Likes",
    formatter: (value) => Number(value).toLocaleString(),
  },
  maxTotalLikes: {
    label: "Max Total Likes",
    formatter: (value) => Number(value).toLocaleString(),
  },
  minPostingFrequency: {
    label: "Min Posting Frequency",
    formatter: (value) => `${value}/mo`,
  },
  maxPostingFrequency: {
    label: "Max Posting Frequency",
    formatter: (value) => `${value}/mo`,
  },
  toneAndApproach: {
    label: "Tone & Approach",
    notKey: "toneAndApproach",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  contentFocus: {
    label: "Content Focus",
    notKey: "contentFocus",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  productionQuality: {
    label: "Production Quality",
    notKey: "productionQuality",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  maleAudiencePercent: {
    label: "Male Audience",
    notKey: "maleAudiencePercent",
    formatter: (value) => (value === "all" ? "All" : `${value}`),
  },
  femaleAudiencePercent: {
    label: "Female Audience",
    notKey: "femaleAudiencePercent",
    formatter: (value) => (value === "all" ? "All" : `${value}`),
  },

  // Candidate-specific filters
  collaborationStatus: {
    label: "Collaboration Status",
    formatter: (value) => (value === "invited" ? "Invited" : value === "not_invited" ? "Not Invited" : "All"),
  },
  minLiveGmv: {
    label: "Min Live GMV",
    formatter: formatCurrency,
  },
  maxLiveGmv: {
    label: "Max Live GMV",
    formatter: formatCurrency,
  },
  minVideoGmv: {
    label: "Min Video GMV",
    formatter: formatCurrency,
  },
  maxVideoGmv: {
    label: "Max Video GMV",
    formatter: formatCurrency,
  },

  // Product Stock Status
  stockStatus: {
    label: "Stock Status",
    notKey: "stockStatus",
    formatter: (value) => {
      switch (value) {
        case "in_stock":
          return "In Stock";
        case "low_stock":
          return "Low Stock";
        case "out_of_stock":
          return "Out of Stock";
        default:
          return String(value);
      }
    },
    arrayDisplay: "join-all",
  },

  // Product performance filters
  minVideos: {
    label: "Min Videos",
    formatter: (value) => Number(value).toLocaleString(),
  },
  maxVideos: {
    label: "Max Videos",
    formatter: (value) => Number(value).toLocaleString(),
  },
  minPrice: {
    label: "Min Price",
    formatter: formatCurrency,
  },
  maxPrice: {
    label: "Max Price",
    formatter: formatCurrency,
  },
  minStockLevel: {
    label: "Min Stock Level",
    formatter: (value) => Number(value).toLocaleString(),
  },
  maxStockLevel: {
    label: "Max Stock Level",
    formatter: (value) => Number(value).toLocaleString(),
  },
  category: {
    label: "Category",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },

  // Video-specific filters
  minViews: {
    label: "Min Views",
    formatter: (value) => {
      const num = Number(value);
      if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
      if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
      return num.toLocaleString();
    },
  },
  maxViews: {
    label: "Max Views",
    formatter: (value) => {
      const num = Number(value);
      if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
      if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
      return num.toLocaleString();
    },
  },
  collaboration: {
    label: "Collaboration",
    notKey: "collaborationIds",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  campaign: {
    label: "Ad Campaign",
    notKey: "campaignIds",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  postedFrom: {
    label: "Posted From",
    formatter: (value) => String(value),
  },
  postedTo: {
    label: "Posted To",
    formatter: (value) => String(value),
  },
  publishDateStart: {
    label: "Published From",
    formatter: (value) => String(value),
  },
  publishDateEnd: {
    label: "Published To",
    formatter: (value) => String(value),
  },
  flaggedDateStart: {
    label: "Flagged From",
    formatter: (value) => String(value),
  },
  flaggedDateEnd: {
    label: "Flagged To",
    formatter: (value) => String(value),
  },
  sequenceRunSequence: {
    label: "Sequence",
    notKey: "sequenceId",
    formatter: (value) => (value as { label: string }).label,
  },
  sequenceRunStatus: {
    label: "Status",
    notKey: "status",
    formatter: (value) => toTitleCase(String(value)),
  },
  videoLanguages: {
    label: "Video Language",
    notKey: "videoLanguages",
    formatter: (value) => (value as { label: string }).label,
    arrayDisplay: "join-all",
  },
  timePeriod: {
    label: "Time Period",
    formatter: (value) => (value === "7d" ? "Last Week" : value === "30d" ? "Last Month" : "All Time"),
  },
  sampleBrandSafety: {
    label: "Brand Safety",
    formatter: (value) => (value === "high" ? "High Risk" : "Low Risk"),
    arrayDisplay: "join-all",
  },
  minPostRate: {
    label: "Min Post Rate",
    formatter: (value) => `${value}%`,
  },
  maxPostRate: {
    label: "Max Post Rate",
    formatter: (value) => `${value}%`,
  },
};

// =============================================================================
// RANGE FILTER PAIRS CONFIGURATION
// =============================================================================
// Defines which filter fields form min/max pairs that should be displayed
// as a single combined pill (e.g., "GMV: £0 - £33K" instead of separate pills)

export type RangeFormat = "currency" | "percent" | "number" | "followers" | "frequency" | "rating" | "date";

export interface RangeFilterPairConfig {
  minField: FilterField;
  maxField: FilterField;
  label: string;
  format: RangeFormat;
  notKey?: string;
}

/**
 * Configuration for all range filter pairs.
 * When both min and max fields of a pair have values, they will be combined
 * into a single pill showing the range.
 */
export const rangeFilterPairs: RangeFilterPairConfig[] = [
  // GMV filters
  { minField: "minGmv", maxField: "maxGmv", label: "GMV", format: "currency", notKey: "gmv" },
  { minField: "minLiveGmv", maxField: "maxLiveGmv", label: "Live GMV", format: "currency", notKey: "liveGmv" },
  { minField: "minVideoGmv", maxField: "maxVideoGmv", label: "Video GMV", format: "currency", notKey: "videoGmv" },

  // Performance metrics
  { minField: "minCtr", maxField: "maxCtr", label: "CTR", format: "percent", notKey: "ctr" },
  { minField: "minCtor", maxField: "maxCtor", label: "CTOR", format: "percent", notKey: "ctor" },
  { minField: "minOrders", maxField: "maxOrders", label: "Orders", format: "number", notKey: "orders" },
  { minField: "minAov", maxField: "maxAov", label: "AOV", format: "currency", notKey: "aov" },
  { minField: "minRevenue", maxField: "maxRevenue", label: "Revenue", format: "currency" },

  // Creator metrics
  { minField: "minFollowers", maxField: "maxFollowers", label: "Followers", format: "followers", notKey: "followers" },
  {
    minField: "minTotalLikes",
    maxField: "maxTotalLikes",
    label: "Total Likes",
    format: "followers",
    notKey: "totalLikes",
  },
  {
    minField: "minPostingFrequency",
    maxField: "maxPostingFrequency",
    label: "Posting Frequency",
    format: "frequency",
    notKey: "postingFrequency",
  },

  // Product metrics
  { minField: "minUnitsSold", maxField: "maxUnitsSold", label: "Units Sold", format: "number", notKey: "unitsSold" },
  { minField: "minRating", maxField: "maxRating", label: "Rating", format: "rating", notKey: "rating" },
  { minField: "minVideos", maxField: "maxVideos", label: "Videos", format: "number", notKey: "videos" },
  { minField: "minPrice", maxField: "maxPrice", label: "Price", format: "currency", notKey: "price" },
  {
    minField: "minStockLevel",
    maxField: "maxStockLevel",
    label: "Stock Level",
    format: "number",
    notKey: "stockLevel",
  },

  // Risk metrics
  {
    minField: "minVideosAtRiskPercent",
    maxField: "maxVideosAtRiskPercent",
    label: "% of Creator's Videos at Risk",
    format: "percent",
    notKey: "videosAtRiskPercent",
  },

  // Video-specific metrics
  { minField: "minViews", maxField: "maxViews", label: "Views", format: "followers", notKey: "views" },

  // Sample Requests metrics
  { minField: "minPostRate", maxField: "maxPostRate", label: "Post Rate", format: "percent", notKey: "postRate" },

  // Publish date range
  { minField: "postedFrom", maxField: "postedTo", label: "Posted", format: "date", notKey: "postedDate" },
  {
    minField: "publishDateStart",
    maxField: "publishDateEnd",
    label: "Publish Date",
    format: "date",
    notKey: "publishDate",
  },

  // Flagged date range (when the risk/flag was detected)
  {
    minField: "flaggedDateStart",
    maxField: "flaggedDateEnd",
    label: "Flagged Date",
    format: "date",
    notKey: "flaggedDate",
  },
];

const rangePairByField = new Map<FilterField, RangeFilterPairConfig>(
  rangeFilterPairs.flatMap((pair) => [
    [pair.minField, pair],
    [pair.maxField, pair],
  ]),
);

export const pillIdOf = (field: FilterField): string => {
  const pair = rangePairByField.get(field);
  return pair ? `${pair.minField}|${pair.maxField}` : field;
};

export type ActivePill =
  | {
      kind: "range";
      id: string;
      pair: RangeFilterPairConfig;
      minValue?: string;
      maxValue?: string;
      fields: FilterField[];
      notKey?: NotKey;
    }
  | { kind: "field"; id: FilterField; field: FilterField; fields: FilterField[]; notKey?: NotKey };

/**
 * The pills shown for the current form values. A range pair becomes one pill with id
 * "minField|maxField"; `fields` are the form fields that pill clears.
 */
export const getActivePills = (values: FiltersFormValues, activeFields: FilterField[]): ActivePill[] => {
  const pills: ActivePill[] = [];
  const seenPairs = new Set<string>();

  for (const field of activeFields) {
    const pair = rangePairByField.get(field);
    if (pair) {
      const id = pillIdOf(field);
      if (seenPairs.has(id)) continue;
      seenPairs.add(id);

      const minValue = values[pair.minField] as string | undefined;
      const maxValue = values[pair.maxField] as string | undefined;
      const fields = [pair.minField, pair.maxField].filter((f) => !isEmptyFilterValue(values[f]));
      if (fields.length === 0) continue;

      pills.push({ kind: "range", id, pair, minValue, maxValue, fields, notKey: pair.notKey });
      continue;
    }

    const config = pillsConfig[field];
    if (!config?.label || isEmptyFilterValue(values[field])) continue;
    pills.push({ kind: "field", id: field, field, fields: [field], notKey: config.notKey });
  }

  return pills;
};

/**
 * Format a numeric value based on the range format type.
 */
export const formatRangeValue = (value: string | number, format: RangeFormat): string => {
  if (format === "date") return String(value);

  const numValue = typeof value === "string" ? Number.parseFloat(value) : value;
  if (!Number.isFinite(numValue)) return String(value);

  switch (format) {
    case "currency": {
      // Compact currency formatting (e.g., £33K, £1.2M)
      if (numValue >= 1_000_000) {
        return `£${(numValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
      }
      if (numValue >= 1_000) {
        return `£${(numValue / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
      }
      return `£${numValue.toLocaleString()}`;
    }
    case "percent":
      return `${numValue}%`;
    case "number":
      return numValue.toLocaleString();
    case "followers": {
      // Compact number formatting (e.g., 33K, 1.2M)
      if (numValue >= 1_000_000) {
        return `${(numValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
      }
      if (numValue >= 1_000) {
        return `${(numValue / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
      }
      return numValue.toLocaleString();
    }
    case "frequency":
      return `${numValue}/mo`;
    case "rating":
      return numValue.toFixed(1);
    default:
      return String(numValue);
  }
};

/**
 * Lookup map for quick access to range pair config by field name.
 * Returns the pair config if the field is part of a range pair.
 */
export const getRangeFilterPair = (field: FilterField): RangeFilterPairConfig | undefined => {
  return rangeFilterPairs.find((pair) => pair.minField === field || pair.maxField === field);
};
